import { Platform } from "react-native";
import { navigationRef } from "@navigations/utils/RootNavigation";
import { store } from "@redux/store";
import {
  setOverlayInactive,
  setOverlayInactiveWeb,
} from "@redux/slices/appStateSlice";

export type CoachOverlayScreen =
  | "ExploreScreen"
  | "BottomTabBar"
  | "MoreScreen"
  | "Favorites"
  | "SearchResult"
  | "Request"
  | "AISearchWidget"
  | "Header"
  | "BulkActions";

export interface CoachOverlayStep {
  key: string;
  text: string;
  order?: number;
}

export interface TourRequest {
  screen: CoachOverlayScreen;
  onStart?: () => void;
  onComplete?: () => void;
  onCancel?: () => void;
  isFocusedCheck?: () => boolean;
  scrollRef?: React.RefObject<any>;
}

type TourState = "idle" | "starting" | "running" | "stopping";

interface ServiceState {
  activeTour: CoachOverlayScreen | null;
  tourState: TourState;
  currentRoute: string | null;
  pendingRequest: TourRequest | null;
}

type MobileRenderer = {
  start: (scrollView?: any) => void;
  stop: () => void;
};

type WebRenderer = {
  startTourForScope: (screen: string) => void;
  resetScopeForScope: (screen: string) => void;
};

type StateChangeListener = (state: ServiceState) => void;

class CoachOverlayServiceClass {
  private state: ServiceState = {
    activeTour: null,
    tourState: "idle",
    currentRoute: null,
    pendingRequest: null,
  };

  private scopedMobileRenderers: Map<CoachOverlayScreen, MobileRenderer> =
    new Map();
  private webRenderer: WebRenderer | null = null;
  private listeners: Set<StateChangeListener> = new Set();
  private navigationUnsubscribe: (() => void) | null = null;
  private startTimeout: ReturnType<typeof setTimeout> | null = null;
  private isInitialized = false;

  initialize() {
    if (this.isInitialized) return;

    this.setupNavigationListener();
    this.isInitialized = true;
  }

  private setupNavigationListener() {
    if (this.navigationUnsubscribe) {
      this.navigationUnsubscribe();
    }

    const checkAndSubscribe = () => {
      if (navigationRef.isReady()) {
        this.updateCurrentRoute();
        this.navigationUnsubscribe = navigationRef.addListener(
          "state",
          this.handleNavigationChange
        );
      } else {
        setTimeout(checkAndSubscribe, 100);
      }
    };

    checkAndSubscribe();
  }

  private handleNavigationChange = () => {
    const previousRoute = this.state.currentRoute;
    this.updateCurrentRoute();

    if (previousRoute && this.state.currentRoute !== previousRoute) {
      this.clearStartTimeout();

      if (this.state.pendingRequest) {
        this.state.pendingRequest = null;
      }

      if (this.state.activeTour) {
        this.stopTour("navigation");
      }
    }
  };

  private updateCurrentRoute() {
    if (!navigationRef.isReady()) return;

    try {
      const state = navigationRef.getRootState();
      const route = this.getActiveRouteName(state);
      this.state.currentRoute = route;
    } catch {
      // Navigation state may not be available during initialization
    }
  }

  private getActiveRouteName(state: any): string {
    if (!state?.routes?.length) return "Unknown";
    const route = state.routes[state.index ?? 0];
    if (route?.state) return this.getActiveRouteName(route.state);
    return route?.name ?? "Unknown";
  }

  registerScopedMobileRenderer(
    scope: CoachOverlayScreen,
    renderer: MobileRenderer
  ) {
    this.scopedMobileRenderers.set(scope, renderer);
  }

  unregisterScopedMobileRenderer(scope: CoachOverlayScreen) {
    this.scopedMobileRenderers.delete(scope);
  }

  registerWebRenderer(renderer: WebRenderer) {
    this.webRenderer = renderer;
  }

  unregisterWebRenderer() {
    this.webRenderer = null;
  }

  requestStart(request: TourRequest): boolean {
    const { screen } = request;

    if (!this.canStartTour(screen)) {
      return false;
    }

    if (this.state.tourState !== "idle") {
      this.state.pendingRequest = request;
      return false;
    }

    this.startTour(request);
    return true;
  }

  private canStartTour(screen: CoachOverlayScreen): boolean {
    const reduxState = store.getState();
    const isWeb = Platform.OS === "web";

    const overlayActive = isWeb
      ? reduxState.appState?.overlayActiveScreensWeb?.[screen]
      : reduxState.appState?.overlayActiveScreens?.[screen];

    if (!overlayActive) {
      return false;
    }

    const overlayConfig = isWeb
      ? reduxState.globalData?.overlay_config_web
      : reduxState.globalData?.overlay_config;

    const screenConfig = overlayConfig?.screens?.[screen];

    if (screenConfig?.overlayActive && !screenConfig?.overlayActive) {
      return false;
    }

    return true;
  }

  private startTour(request: TourRequest) {
    const { screen, onStart, isFocusedCheck, scrollRef } = request;

    this.clearStartTimeout();

    if (isFocusedCheck && !isFocusedCheck()) {
      return;
    }

    const isWeb = Platform.OS === "web";
    if (!isWeb && !this.scopedMobileRenderers.has(screen)) {
      return;
    }

    this.updateState({
      activeTour: screen,
      tourState: "starting",
    });

    this.startTimeout = setTimeout(() => {
      if (isFocusedCheck && !isFocusedCheck()) {
        this.updateState({
          activeTour: null,
          tourState: "idle",
        });
        return;
      }

      if (!isWeb && !this.scopedMobileRenderers.has(screen)) {
        this.updateState({
          activeTour: null,
          tourState: "idle",
        });
        return;
      }

      if (this.state.activeTour !== screen) {
        return;
      }

      this.updateState({ tourState: "running" });

      if (isWeb && this.webRenderer) {
        this.webRenderer.startTourForScope(screen);
      } else if (!isWeb) {
        const scopedRenderer = this.scopedMobileRenderers.get(screen);
        if (scopedRenderer) {
          try {
            // Pass scrollRef to copilot for proper positioning
            scopedRenderer.start(scrollRef?.current);
          } catch (e) {
            this.updateState({
              activeTour: null,
              tourState: "idle",
            });
            return;
          }
        }
      }

      onStart?.();
    }, 300);
  }

  stopTour(reason: "complete" | "cancel" | "navigation" = "cancel") {
    if (!this.state.activeTour) {
      return;
    }

    this.clearStartTimeout();

    const screen = this.state.activeTour;
    const pendingRequest = this.state.pendingRequest;

    this.updateState({
      tourState: "stopping",
    });

    const isWeb = Platform.OS === "web";

    try {
      if (isWeb && this.webRenderer) {
        this.webRenderer.resetScopeForScope(screen);
      } else if (!isWeb) {
        const scopedRenderer = this.scopedMobileRenderers.get(screen);
        if (scopedRenderer) {
          scopedRenderer.stop();
        }
      }
    } catch (e) {
      // Stop may fail if component already unmounted
    }

    if (reason === "complete") {
      this.markScreenComplete(screen);
    }

    this.updateState({
      activeTour: null,
      tourState: "idle",
      pendingRequest: null,
    });

    if (pendingRequest && reason !== "navigation") {
      setTimeout(() => {
        if (!isWeb && !this.scopedMobileRenderers.has(pendingRequest.screen)) {
          return;
        }
        this.requestStart(pendingRequest);
      }, 200);
    }
  }

  completeTour() {
    this.stopTour("complete");
  }

  cancelTour() {
    this.stopTour("cancel");
  }

  private markScreenComplete(screen: CoachOverlayScreen) {
    const isWeb = Platform.OS === "web";

    if (isWeb) {
      store.dispatch(setOverlayInactiveWeb({ screen }));
    } else {
      store.dispatch(setOverlayInactive({ screen }));
    }
  }

  private clearStartTimeout() {
    if (this.startTimeout) {
      clearTimeout(this.startTimeout);
      this.startTimeout = null;
    }
  }

  getState(): Readonly<ServiceState> {
    return { ...this.state };
  }

  isRunning(): boolean {
    return this.state.tourState === "running";
  }

  getActiveTour(): CoachOverlayScreen | null {
    return this.state.activeTour;
  }

  isRendererAvailable(screen: CoachOverlayScreen): boolean {
    const isWeb = Platform.OS === "web";
    if (isWeb) {
      return this.webRenderer !== null;
    }
    return this.scopedMobileRenderers.has(screen);
  }

  forceStopAll() {
    this.clearStartTimeout();

    if (this.state.activeTour) {
      const isWeb = Platform.OS === "web";

      try {
        if (isWeb && this.webRenderer) {
          this.webRenderer.resetScopeForScope(this.state.activeTour);
        } else if (!isWeb) {
          this.scopedMobileRenderers.forEach((renderer) => {
            try {
              renderer.stop();
            } catch {
              // Ignore errors
            }
          });
        }
      } catch {
        // Ignore errors during force stop
      }
    }

    this.updateState({
      activeTour: null,
      tourState: "idle",
      pendingRequest: null,
    });
  }

  subscribe(listener: StateChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private updateState(partial: Partial<ServiceState>) {
    this.state = { ...this.state, ...partial };
    this.notifyListeners();
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => {
      try {
        listener(this.getState());
      } catch (error) {
        console.error("CoachOverlayService listener error:", error);
      }
    });
  }

  destroy() {
    this.clearStartTimeout();

    if (this.navigationUnsubscribe) {
      this.navigationUnsubscribe();
      this.navigationUnsubscribe = null;
    }

    this.listeners.clear();
    this.scopedMobileRenderers.clear();
    this.webRenderer = null;
    this.isInitialized = false;

    this.state = {
      activeTour: null,
      tourState: "idle",
      currentRoute: null,
      pendingRequest: null,
    };
  }
}

export const CoachOverlayService = new CoachOverlayServiceClass();
